/**
 * A ~50-line reactive core: `signal` holds a value, `effect` re-runs whenever
 * a signal it read changes, and `computed` derives a value reactively. Enough
 * to wire controls to live readouts and redraws without a framework.
 */

interface Reaction {
  run: () => void;
  deps: Set<Set<Reaction>>;
}

let active: Reaction | null = null;

export interface ReadonlySignal<T> {
  readonly value: T;
}
export interface Signal<T> extends ReadonlySignal<T> {
  value: T;
}

export function signal<T>(initial: T): Signal<T> {
  let val = initial;
  const subs = new Set<Reaction>();
  return {
    get value(): T {
      if (active) {
        subs.add(active);
        active.deps.add(subs);
      }
      return val;
    },
    set value(next: T) {
      if (Object.is(val, next)) return;
      val = next;
      for (const r of [...subs]) r.run();
    },
  };
}

/** Run `fn` now and again whenever any signal it read changes. Returns a stop fn. */
export function effect(fn: () => void): () => void {
  const reaction: Reaction = {
    deps: new Set(),
    run() {
      cleanup(reaction);
      const prev = active;
      active = reaction;
      try {
        fn();
      } finally {
        active = prev;
      }
    },
  };
  reaction.run();
  return () => cleanup(reaction);
}

export function computed<T>(fn: () => T): ReadonlySignal<T> {
  const out = signal<T>(undefined as unknown as T);
  effect(() => {
    out.value = fn();
  });
  return out;
}

function cleanup(reaction: Reaction): void {
  for (const dep of reaction.deps) dep.delete(reaction);
  reaction.deps.clear();
}
